import React from "react";
import { FaEdit } from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTag } from "@fortawesome/free-solid-svg-icons";
import {
  formatPrice,
  getCartTotal,
  getProductPrice,
} from "../../utils/cart.utils";
import ModalComponent from "../../components/atoms/modal/modal.component";

const qtyInputStyle = {
  width: 72,
  height: 36,
  border: "1px solid var(--stroke-1)",
  borderRadius: "var(--radius-md)",
  padding: "0 10px",
  fontFamily: "var(--font-mono)",
  fontWeight: 600,
  textAlign: "center",
  color: "var(--fg-1)",
};

const CartTemplate = ({ templateData, templateEvent }) => {
  const {
    cart,
    user,
    address: { shipping, billing, ...address },
    showCheckout,
    deleteModal,
    activeTab,
    selectedShippingId,
    promoCode,
    showPromoInput,
    promoCodeError,
    appliedPromo,
  } = templateData;
  const {
    handleLoginClick,
    handleQtyChange,
    handleCartDelete,
    handleCartDeleteConfirm,
    handleManualQtyChange,
    handleProceedCheckout,
    handleChange,
    handlePayNow,
    handleNext,
    handleBack,
    setActiveTab,
    handleAddAddress,
    handleEditAddress,
    setSelectedShippingId,
    setPromoCode,
    setAppliedPromo,
    setShowPromoInput,
    handleApplyPromoCode,
  } = templateEvent;

  const { subTotal, total, gst, discount, hasPromo } = getCartTotal(
    cart,
    appliedPromo,
  );

  // Address lines (reused across address/billing/review)
  const addrLines = (a) => (
    <p style={{ color: "var(--fg-2)", margin: 0, lineHeight: 1.6 }}>
      {a.invoice_to || "Address"}
      <br />
      {a.address_line1 && (<>{a.address_line1}<br /></>)}
      {a.address_line2 && (<>{a.address_line2}<br /></>)}
      {a.city && (<>{a.city}{a.state && `, ${a.state}`}<br /></>)}
      {a.pincode && (<>{a.pincode}<br /></>)}
      {a.contact_person_number}
    </p>
  );

  const summaryRows = (
    <>
      <div className="ds-summary-row"><span>Subtotal</span><span className="val">₹{subTotal}</span></div>
      {hasPromo && (
        <>
          <div className="ds-summary-row"><span>Promo ({appliedPromo?.code})</span><span className="val discount">−₹{discount}</span></div>
          <div className="ds-summary-row"><span>After discount</span><span className="val">₹{formatPrice(parseFloat(subTotal) - parseFloat(discount))}</span></div>
        </>
      )}
      <div className="ds-summary-row"><span>GST (18%)</span><span className="val">₹{gst}</span></div>
      <div className="ds-summary-row total"><span>Grand total</span><span className="val">₹{total}</span></div>
    </>
  );

  const renderCartView = () => (
    <div className="ds-container">
      <div className="ds-page-head">
        <h1>Your cart</h1>
        <p className="sub">{cart?.length || 0} line item{cart?.length === 1 ? "" : "s"}</p>
      </div>

      <div className="ds-grid-cart" style={{ paddingBottom: 56 }}>
        <div className="ds-table-wrap">
          <table className="ds-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Qty</th>
                <th className="num">Unit price</th>
                <th className="num">Extended</th>
              </tr>
            </thead>
            <tbody>
              {!cart?.length ? (
                <tr>
                  <td colSpan="4">
                    <div className="ds-state" style={{ boxShadow: "none", border: 0 }}>
                      <h3>Your cart is empty</h3>
                      <p>No items found in your cart. Continue shopping to add items.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                cart.map((item, index) => {
                  const price = getProductPrice(item);
                  return (
                    <tr key={index}>
                      <td>
                        <div className="ds-row" style={{ flexWrap: "nowrap", alignItems: "flex-start" }}>
                          <span className="ds-thumb" style={{ width: 64, height: 64, aspectRatio: "auto", borderRadius: 6, flex: "0 0 auto" }}>
                            {item?.image ? <img src={item.image} alt="" /> : null}
                          </span>
                          <div style={{ minWidth: 0 }}>
                            <div className="mpn">{item?.part_number}</div>
                            <div className="ds-maker">{item.make}</div>
                            <div className="ds-desc">{item.description}</div>
                            <a className="ds-link" style={{ cursor: "pointer", color: "var(--danger)", fontSize: 12 }} onClick={() => handleCartDelete(index)}>
                              Remove
                            </a>
                          </div>
                        </div>
                      </td>
                      <td>
                        <input
                          style={qtyInputStyle}
                          type="text"
                          name="quantity"
                          min="10"
                          value={item.required_qty}
                          onChange={(e) => handleManualQtyChange({ value: e.target.value, item: item, index: index })}
                        />
                      </td>
                      <td className="price">₹{price}</td>
                      <td className="price">₹{formatPrice(item.required_qty * price)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <aside className="ds-summary">
          <h3>Order summary</h3>
          {summaryRows}
          <button className="ds-btn ds-btn-primary ds-btn-block ds-btn-lg ds-mt-l" onClick={handleProceedCheckout}>
            Proceed to checkout
          </button>
        </aside>
      </div>
    </div>
  );

  const addressCard = (item, title, onEdit, selectable) => (
    <div className="ds-panel ds-panel-p" style={{ position: "relative" }}>
      <div className="ds-row ds-between" style={{ marginBottom: 12 }}>
        {selectable ? (
          <label className="ds-row" style={{ cursor: "pointer", gap: 8 }}>
            <input
              type="radio"
              name="shippingAddress"
              checked={selectedShippingId === item.id}
              onChange={() => setSelectedShippingId(item.id)}
              style={{ accentColor: "var(--brand-violet)", cursor: "pointer" }}
            />
            <span className="ds-eyebrow" style={{ margin: 0 }}>{title}</span>
          </label>
        ) : (
          <span className="ds-eyebrow" style={{ margin: 0 }}>{title}</span>
        )}
        {onEdit && (
          <a className="ds-link" style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }} onClick={(e) => { e.preventDefault(); onEdit(); }}>
            <FaEdit /> Edit
          </a>
        )}
      </div>
      {addrLines(item)}
    </div>
  );

  const renderCheckoutView = () => (
    <div className="ds-container">
      <div className="ds-head-row" style={{ alignItems: "center", marginBlock: "16px 24px" }}>
        <button className="ds-btn ds-btn-ghost" onClick={handleBack}>← Back to cart</button>
        <h1 style={{ fontSize: 24 }}>Checkout</h1>
      </div>

      <div className="ds-grid-cart">
        <div>
          <div className="ds-tabs" data-tabs style={{ marginBottom: 20 }}>
            <button className={`ds-tab ${activeTab === "address" ? "active" : ""}`} onClick={() => setActiveTab("address")}>Address</button>
            <button className={`ds-tab ${activeTab === "payment" ? "active" : ""}`} onClick={() => setActiveTab("payment")}>Payment</button>
            <button className={`ds-tab ${activeTab === "review" ? "active" : ""}`} onClick={() => setActiveTab("review")}>Review</button>
          </div>

          {/* ADDRESS */}
          {activeTab === "address" && (
            <div className="ds-stack">
              <div className="ds-head-row">
                <h3>Select delivery address</h3>
                <button className="ds-btn ds-btn-secondary ds-btn-sm" onClick={() => handleAddAddress()}>+ Add new address</button>
              </div>
              <div className="ds-grid-2">
                {Array.isArray(shipping) && shipping.length > 0 ? (
                  shipping.map((item) => (
                    <div key={item.id}>{addressCard(item, "Shipping address", () => handleEditAddress(item.id), true)}</div>
                  ))
                ) : (
                  <p className="ds-muted">No shipping address found. Please add a new address.</p>
                )}
                {billing && addressCard(billing, "Billing address", () => handleEditAddress("billing"), false)}
              </div>
              <div className="ds-row ds-between ds-mt-l">
                <button className="ds-btn ds-btn-ghost" onClick={handleBack}>Back to cart</button>
                <button className="ds-btn ds-btn-primary" onClick={handleNext}>Proceed to payment →</button>
              </div>
            </div>
          )}

          {/* PAYMENT */}
          {activeTab === "payment" && (
            <div className="ds-stack">
              <div className="ds-panel ds-panel-p ds-stack">
                <h3>Select payment method</h3>
                <label className="ds-panel ds-panel-p ds-row ds-between" style={{ cursor: "pointer", boxShadow: "none" }}>
                  <span className="ds-row"><img src="assets/img/gpay.png" alt="Google Pay" style={{ height: 24 }} /> Google Pay</span>
                  <input type="radio" name="payment" style={{ accentColor: "var(--brand-violet)" }} />
                </label>
                <label className="ds-panel ds-panel-p ds-row ds-between" style={{ cursor: "pointer", boxShadow: "none" }}>
                  <span className="ds-row"><img src="assets/img/paypal.png" alt="PayPal" style={{ height: 24 }} /> PayPal</span>
                  <input type="radio" name="payment" style={{ accentColor: "var(--brand-violet)" }} />
                </label>

                <div>
                  <p className="ds-eyebrow">Promo code</p>
                  {showPromoInput ? (
                    <>
                      <div className="ds-row" style={{ flexWrap: "nowrap" }}>
                        <div className="ds-input" style={{ flex: 1 }}>
                          <input
                            type="text"
                            placeholder="Enter promo code"
                            value={promoCode}
                            onChange={(e) => setPromoCode(e.target.value)}
                            disabled={!!appliedPromo}
                          />
                        </div>
                        {appliedPromo ? (
                          <button className="ds-btn ds-btn-secondary ds-btn-sm" onClick={() => { setAppliedPromo(null); setPromoCode(""); setShowPromoInput(false); }}>Remove</button>
                        ) : (
                          <button className="ds-btn ds-btn-primary ds-btn-sm" onClick={() => handleApplyPromoCode(promoCode)}>Apply</button>
                        )}
                      </div>
                      {promoCodeError && <div style={{ color: "var(--danger)", marginTop: 8 }}>{promoCodeError}</div>}
                    </>
                  ) : (
                    <a className="ds-link ds-row" style={{ cursor: "pointer", gap: 6 }} onClick={() => setShowPromoInput(!showPromoInput)}>
                      <FontAwesomeIcon icon={faTag} />
                      {appliedPromo ? `Promo Code: ${appliedPromo.code} (Applied)` : "+ Add Promo Code"}
                    </a>
                  )}
                </div>
              </div>
              <div className="ds-row ds-between ds-mt-l">
                <button className="ds-btn ds-btn-ghost" onClick={handleBack}>← Back to address</button>
                <button className="ds-btn ds-btn-primary" onClick={handleNext}>Review order →</button>
              </div>
            </div>
          )}

          {/* REVIEW */}
          {activeTab === "review" && (
            <div className="ds-stack">
              <div className="ds-panel ds-panel-p ds-stack">
                <h3>Review your order</h3>
                <div className="ds-grid-2">
                  {address?.shipping && selectedShippingId && (() => {
                    const selectedAddress = Array.isArray(address.shipping)
                      ? address.shipping.find((addr) => addr.id === selectedShippingId)
                      : address.shipping;
                    return selectedAddress ? (
                      <div>
                        <p className="ds-eyebrow">Shipping address</p>
                        {addrLines(selectedAddress)}
                      </div>
                    ) : null;
                  })()}
                  {billing && (
                    <div>
                      <p className="ds-eyebrow">Billing address</p>
                      {addrLines(billing)}
                    </div>
                  )}
                </div>

                <div className="ds-table-wrap">
                  <table className="ds-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th className="num">Qty</th>
                        <th className="num">Price</th>
                        <th className="num">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cart?.map((item, index) => {
                        const price = getProductPrice(item);
                        return (
                          <tr key={index}>
                            <td className="mpn">{item.part_number}</td>
                            <td className="num">{item.required_qty}</td>
                            <td className="price">₹{price}</td>
                            <td className="price">₹{formatPrice(item.required_qty * price)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="ds-row ds-between ds-mt-l">
                <button className="ds-btn ds-btn-ghost" onClick={handleBack}>← Back to payment</button>
                <button className="ds-btn ds-btn-primary ds-btn-lg" onClick={handlePayNow}>Place order</button>
              </div>
            </div>
          )}
        </div>

        <aside className="ds-summary">
          <h3>Order summary</h3>
          {summaryRows}
        </aside>
      </div>
    </div>
  );

  return (
    <>
      {showCheckout ? renderCheckoutView() : renderCartView()}
      <ModalComponent
        open={deleteModal.show}
        title={"Delete !"}
        message={"Are you sure to delete?"}
        primaryText={"Delete"}
        secondaryText={"Cancel"}
        primaryAction={handleCartDeleteConfirm}
        secondaryAction={handleCartDelete}
      />
    </>
  );
};

export default CartTemplate;
